import React, { useEffect, useRef, useState } from "react";
import { requestLogs } from "../../../redux/actions/logsRequest.actions";
import { AppDispatch } from "../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { useNavigate } from "react-router-dom";
import ModalShell, { ModalLoading } from "./ModalShell";

interface ModalProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  spas: any[];
  singleSpaConnected?: boolean|null;
}

const RequestLogsModal: React.FC<ModalProps> = ({ isOpen, setOpen, spas, singleSpaConnected=null }) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { data: logs, loading: isLoading, error } = useSelector(
    (state: RootState) => state.logsRequest
  );

  const [offlineCount, setOfflineCount] = useState(0);
  const [noConnectedSpas, setNoConnectedSpas] = useState(false);
  const hasRunForOpenRef = useRef(false);

  const handleClose = () => {
    setOpen(false);
  };

  // Reset run flag when modal closes so next open runs again
  useEffect(() => {
    if (!isOpen) {
      hasRunForOpenRef.current = false;
      setOfflineCount(0);
      setNoConnectedSpas(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || hasRunForOpenRef.current || !spas?.length) {
      if (isOpen && spas?.length === 0) {
        hasRunForOpenRef.current = true;
        setNoConnectedSpas(true);
        setOfflineCount(0);
      }
      return;
    }

    hasRunForOpenRef.current = true;
    const connected = spas.filter((spa: any) => spa.is_connected || singleSpaConnected);
    const countOffline = spas.length - connected.length;
    setOfflineCount(countOffline);

    if (connected.length === 0) {
      setNoConnectedSpas(true);
      return;
    }

    setNoConnectedSpas(false);
    (async () => {
      await Promise.all(
        connected.map((spa: any) => dispatch(requestLogs({ guid: spa?.id })))
      );
    })();
  }, [dispatch, spas, isOpen, singleSpaConnected]);

  const offlineNote = offlineCount > 0 && (
    <p className="dlg-muted">
      {offlineCount === 1 ? "1 spa was offline" : `${offlineCount} spas were offline`} and could not receive a request.
    </p>
  );

  // All spas were offline — no requests sent; show result so we don't spin forever
  if (noConnectedSpas) {
    return (
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        size="sm"
        tone="warning"
        title="No connected spas"
        actions={<button className="btn btn-primary" onClick={handleClose}>Close</button>}
      >
        <p>{offlineCount === 1 ? "The spa was offline." : "The spas were offline."} Log requests could not be sent.</p>
        <p className="dlg-muted">
          {offlineCount === 1 ? "Connect the spa and try again to request logs." : "Connect spas and try again to request logs."}
        </p>
      </ModalShell>
    );
  }

  if (logs && !isLoading) {
    return (
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        size="sm"
        tone="success"
        title="Request sent"
        actions={
          <>
            <button className="btn" onClick={handleClose}>Close</button>
            <button className="btn btn-primary" onClick={() => navigate(`/logs`)}>Go to Logs</button>
          </>
        }
      >
        {(logs.message ?? logs.status) && <div className="dlg-notice success">{logs.message ?? logs.status}</div>}
        <p>Log requests have been sent. You can view and download logs from the Logs page.</p>
        {offlineNote}
      </ModalShell>
    );
  }

  if (error && !isLoading) {
    return (
      <ModalShell
        open={isOpen}
        onClose={handleClose}
        size="sm"
        tone="error"
        title="Request failed"
        actions={<button className="btn btn-primary" onClick={handleClose}>OK</button>}
      >
        <div className="dlg-notice error">{error}</div>
        <p>The log request could not be completed. Please try again or contact support if the issue persists.</p>
        {offlineNote}
      </ModalShell>
    );
  }

  // Loading, or open but not yet loading/success/error (e.g. right after open)
  return (
    <ModalShell open={isOpen} onClose={handleClose} size="sm" title="Requesting logs…">
      <ModalLoading label="Asking the spa to upload its logs."/>
    </ModalShell>
  );
};

export default RequestLogsModal;
